import { useState } from 'react'

import dayjs from 'dayjs'

import {
  useIncentiveTargetsApi,
  useMarkIncentiveProcessedApi
} from '@/common/api/modules/ugcMail'
import { UGCMailApiPath } from '@/common/api/params/ugcMail'
import { LocalStorage, loadStorage } from '@/common/util/storage'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * 投稿特典の対象者。
 *
 * 対象の抽出・重複除外・処理済み管理はシステムが自動で行い、
 * **人がやるのは W2Commerce 側での付与だけ**にする。
 * 「前回どこまで処理したか」を人が覚える運用にすると、
 * 二重付与か漏れのどちらかが起きる。
 *
 * 流れ: 未処理一覧 → 選択してCSV → W2側で付与 → 選択分を処理済みにする。
 */

const fmt = (v: string | null) => (v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '—')

const IncentivePanel = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = useIncentiveTargetsApi({ site_id: siteId, limit: 200 })
  const [checked, setChecked] = useState<Set<number>>(new Set())
  const [message, setMessage] = useState<string | null>(null)

  const { trigger: mark, isMutating: marking } = useMarkIncentiveProcessedApi({
    onSuccess: (r) => {
      setMessage(`${r.marked}件を処理済みにしました`)
      setChecked(new Set())
      mutate()
    },
    onError: (e) => setMessage(e instanceof Error ? e.message : '処理済みにできませんでした')
  })

  const items = data?.items ?? []
  const allChecked = items.length > 0 && items.every((i) => checked.has(i.id))

  const toggleAll = () => {
    setChecked(allChecked ? new Set() : new Set(items.map((i) => i.id)))
  }
  const toggle = (id: number) => {
    setChecked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const downloadSelected = async () => {
    if (checked.size === 0) {
      setMessage('対象を選択してください')
      return
    }
    setMessage(null)
    // 選択したIDだけをCSVに出す。blobリンクでダウンロード
    const path = UGCMailApiPath.INCENTIVE_CSV.replace('{site_id}', String(siteId))
      .replace('date_from={date_from}&date_to={date_to}', `ids=${[...checked].join(',')}`)
    const res = await fetch(path, {
      headers: { Authorization: `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}` }
    })
    if (!res.ok) {
      setMessage('ダウンロードできませんでした')
      return
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `レビュー投稿特典対象_${dayjs().format('YYYYMMDD')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Area>
      <Heading subText='未処理の投稿完了だけが並びます。CSVで書き出し、W2Commerce側で付与したら「処理済みにする」を押してください。'>
        投稿特典の対象者
      </Heading>
      <AreaContent>
        {isLoading || !data ? (
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        ) : (
          <>
            <div className='flex flex-wrap items-center gap-3'>
              <Text size={12} color={TextColor.SECONDARY}>
                未処理 {data.total} 件 / 処理済み {data.processed_total} 件
              </Text>
              <div className='ml-auto flex items-center gap-2'>
                <Button
                  color={ButtonColor.SECONDARY}
                  disabled={checked.size === 0}
                  onClick={downloadSelected}
                >
                  選択分をCSV（{checked.size}件）
                </Button>
                <Button
                  color={ButtonColor.PRIMARY}
                  disabled={checked.size === 0 || marking}
                  loading={marking}
                  onClick={() => mark({ site_id: siteId, ids: [...checked] })}
                >
                  選択分を処理済みにする
                </Button>
              </div>
            </div>

            {message && (
              <div className='mt-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  {message}
                </Text>
              </div>
            )}

            {items.length === 0 ? (
              <div className='mt-3'>
                <Text size={13} color={TextColor.SECONDARY}>
                  未処理の対象はありません。投稿が完了すると、ここに自動で並びます。
                </Text>
              </div>
            ) : (
              <div className='mt-3 overflow-x-auto'>
                <Table>
                  <thead>
                    <TableRow head>
                      <TableHeadItem>
                        <input type='checkbox' checked={allChecked} onChange={toggleAll} />
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>投稿日時</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>お客様</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>受注ID</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>商品</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>星</Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {items.map((i) => (
                      <TableRow key={i.id}>
                        <TableRowItem>
                          <input
                            type='checkbox'
                            checked={checked.has(i.id)}
                            onChange={() => toggle(i.id)}
                          />
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{fmt(i.submitted_at)}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{i.customer_name || '—'}</Text>
                          <Text size={10} color={TextColor.TERTIARY}>
                            {i.customer_email || '—'}
                          </Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{i.order_id || '—'}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{i.product_name || i.product_external_id || '—'}</Text>
                        </TableRowItem>
                        <TableRowItem>
                          <Text size={12}>{i.rating ?? '—'}</Text>
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </AreaContent>
    </Area>
  )
}

export default IncentivePanel
